// 세운(歲運) - 올해 운세 계산
import type { SajuResult, Element } from './sajuCalculations';
import { HEAVENLY_STEMS as STEMS, EARTHLY_BRANCHES as BRANCHES, STEM_ELEMENTS, BRANCH_ELEMENTS } from './sajuCalculations';

// 2026년 (병오년)
const CURRENT_YEAR = 2026;
const CURRENT_STEM_IDX = 2; // 병(丙)
const CURRENT_BRANCH_IDX = 6; // 오(午)

// 세운 분석 결과
export interface SewoonResult {
  year: number;
  yearGanji: string;
  yearElement: Element;
  overallScore: number;
  overallLevel: 'excellent' | 'good' | 'normal' | 'challenging' | 'difficult';
  summary: string;
  categories: {
    career: { score: number; trend: 'up' | 'stable' | 'down'; message: string };
    love: { score: number; trend: 'up' | 'stable' | 'down'; message: string };
    money: { score: number; trend: 'up' | 'stable' | 'down'; message: string };
    health: { score: number; trend: 'up' | 'stable' | 'down'; message: string };
  };
  monthly: MonthlyFortune[];
  advice: string[];
  lucky: {
    colors: string[];
    numbers: number[];
    directions: string[];
  };
}

// 월별 운세
export interface MonthlyFortune {
  month: number;
  ganji: string;
  score: number;
  highlight: string;
  message: string;
}

// 오행 상생
const SHENG_CYCLE: Record<Element, Element> = {
  목: '화',
  화: '토',
  토: '금',
  금: '수',
  수: '목',
};

// 오행 상극
const KE_CYCLE: Record<Element, Element> = {
  목: '토',
  토: '수',
  수: '화',
  화: '금',
  금: '목',
};

// 세운 계산
export function calculateSewoon(saju: SajuResult, lang: 'ko' | 'en' = 'ko'): SewoonResult {
  const dayStem = saju.dayPillar.stemIdx;
  const dayBranch = saju.dayPillar.branchIdx;
  const dayElement = STEM_ELEMENTS[dayStem];

  const yearStem = CURRENT_STEM_IDX;
  const yearBranch = CURRENT_BRANCH_IDX;
  const yearElement = STEM_ELEMENTS[yearStem];

  // 기본 점수 계산
  let baseScore = 50;

  // 일간과 년간의 관계
  const stemDiff = (yearStem - dayStem + 10) % 10;
  if (stemDiff === 0) baseScore += 5; // 비견
  else if (stemDiff === 1) baseScore += 3; // 겁재
  else if (stemDiff === 2) baseScore += 15; // 식신 - 좋음
  else if (stemDiff === 3) baseScore += 12; // 상관
  else if (stemDiff === 4) baseScore += 20; // 편재 - 최고
  else if (stemDiff === 5) baseScore += 18; // 정재 - 좋음
  else if (stemDiff === 6) baseScore += 8; // 편관
  else if (stemDiff === 7) baseScore += 10; // 정관
  else if (stemDiff === 8) baseScore += 15; // 편인 - 좋음
  else if (stemDiff === 9) baseScore += 12; // 정인

  // 일지와 년지의 관계
  const branchDiff = (yearBranch - dayBranch + 12) % 12;
  if (branchDiff === 0) baseScore += 0; // 자신
  else if (branchDiff === 6) baseScore -= 5; // 충
  else if ([4, 8].includes(branchDiff)) baseScore += 8; // 삼합
  else if ([2, 10].includes(branchDiff)) baseScore += 5; // 육합
  else baseScore += 2;

  // 오행 관계
  if (SHENG_CYCLE[dayElement] === yearElement) {
    baseScore += 10; // 내가 낳는 것 - 재물운
  } else if (SHENG_CYCLE[yearElement] === dayElement) {
    baseScore += 8; // 나를 낳는 것 - 귀인운
  } else if (KE_CYCLE[dayElement] === yearElement) {
    baseScore += 5; // 내가 극하는 것 - 관운
  } else if (KE_CYCLE[yearElement] === dayElement) {
    baseScore -= 5; // 나를 극하는 것 - 압박
  }

  // 신강약 반영
  if (saju.sinGangYak?.level === 'strong') {
    baseScore += 5; // 신강하면 재물/관운 잘 받음
  } else if (saju.sinGangYak?.level === 'weak') {
    baseScore += 3; // 신약하면 인성으로 보호받음
  }

  const overallScore = Math.min(100, Math.max(20, Math.round(baseScore)));

  // 레벨 판정
  let overallLevel: SewoonResult['overallLevel'];
  if (overallScore >= 80) overallLevel = 'excellent';
  else if (overallScore >= 65) overallLevel = 'good';
  else if (overallScore >= 50) overallLevel = 'normal';
  else if (overallScore >= 35) overallLevel = 'challenging';
  else overallLevel = 'difficult';

  // 요약 메시지
  const summaries: Record<string, Record<string, string>> = {
    ko: {
      excellent: '2026년은 당신에게 빛나는 한 해가 될 거예요. 기회가 많이 찾아오니 적극적으로 잡으세요!',
      good: '2026년은 전반적으로 좋은 흐름이에요. 노력한 만큼 결과가 따라주는 해예요.',
      normal: '2026년은 무난하게 흘러가는 해예요. 큰 변화보다는 현상 유지에 집중하세요.',
      challenging: '2026년은 조금 도전적인 해가 될 수 있어요. 무리한 도전보다는 안정을 우선으로 두세요.',
      difficult: '2026년은 힘든 시기가 될 수 있어요. 무리하지 말고 체력 관리와 마음의 여유를 가지세요.',
    },
    en: {
      excellent: '2026 is set to be a brilliant year for you. Opportunities abound — seize them proactively!',
      good: '2026 flows well overall. Your efforts will be rewarded accordingly.',
      normal: '2026 will pass smoothly. Focus on maintaining your current position rather than big changes.',
      challenging: '2026 may be a somewhat challenging year. Prioritize stability over bold risks.',
      difficult: '2026 may be a tough period. Take care of your health and give yourself mental space.',
    },
  };

  // 카테고리별 분석
  const categories = analyzeCategories(saju, overallScore, stemDiff, lang);

  // 월별 운세
  const monthly = generateMonthlyFortune(saju, lang);

  // 조언
  const advice = generateAdvice(overallLevel, stemDiff, saju, lang);

  // 행운 정보
  const lucky = generateLuckyInfo(saju, yearElement, lang);

  return {
    year: CURRENT_YEAR,
    yearGanji: `${STEMS[yearStem]}${BRANCHES[yearBranch]}`,
    yearElement,
    overallScore,
    overallLevel,
    summary: summaries[lang][overallLevel],
    categories,
    monthly,
    advice,
    lucky,
  };
}

// 카테고리별 분석
function analyzeCategories(
  saju: SajuResult,
  baseScore: number,
  stemDiff: number,
  lang: 'ko' | 'en' = 'ko'
): SewoonResult['categories'] {
  const isStrong = saju.sinGangYak?.level === 'strong';

  // 커리어
  let careerScore = baseScore;
  let careerTrend: SewoonResult['categories']['career']['trend'] = 'stable';
  if ([4, 5, 6, 7].includes(stemDiff)) {
    careerScore += 10; // 재성/관성 - 커리어 운 상승
    careerTrend = 'up';
  } else if ([2, 3].includes(stemDiff)) {
    careerScore += 5;
    careerTrend = 'up';
  }

  // 연애
  let loveScore = baseScore;
  let loveTrend: SewoonResult['categories']['love']['trend'] = 'stable';
  if ([4, 5].includes(stemDiff)) {
    loveScore += 8;
    loveTrend = 'up';
  } else if ([6, 7].includes(stemDiff)) {
    loveScore += 5;
    loveTrend = stemDiff === 7 ? 'up' : 'stable';
  }

  // 금전
  let moneyScore = baseScore;
  let moneyTrend: SewoonResult['categories']['money']['trend'] = 'stable';
  if ([4, 5].includes(stemDiff)) {
    moneyScore += isStrong ? 15 : 10;
    moneyTrend = 'up';
  } else if ([6, 7].includes(stemDiff)) {
    moneyScore += 5;
    moneyTrend = 'stable';
  }

  // 건강
  let healthScore = baseScore;
  let healthTrend: SewoonResult['categories']['health']['trend'] = 'stable';
  if ([8, 9].includes(stemDiff)) {
    healthScore += 8; // 인성 - 건강 보호
  } else if ([2, 3].includes(stemDiff)) {
    healthScore -= 5; // 식상 - 소모
    healthTrend = 'down';
  }

  return {
    career: {
      score: Math.min(100, careerScore),
      trend: careerTrend,
      message: getCareerMessage(careerScore, stemDiff, lang),
    },
    love: {
      score: Math.min(100, loveScore),
      trend: loveTrend,
      message: getLoveMessage(loveScore, stemDiff, lang),
    },
    money: {
      score: Math.min(100, moneyScore),
      trend: moneyTrend,
      message: getMoneyMessage(moneyScore, stemDiff, isStrong, lang),
    },
    health: {
      score: Math.min(100, healthScore),
      trend: healthTrend,
      message: getHealthMessage(healthScore, stemDiff, lang),
    },
  };
}

function getCareerMessage(score: number, _stemDiff: number, lang: 'ko' | 'en' = 'ko'): string {
  if (lang === 'en') {
    if (score >= 80) return 'A promotion, job change, or new opportunity may come your way!';
    if (score >= 65) return 'Your work and efforts are likely to be recognized this year.';
    if (score >= 50) return 'Career progresses smoothly without major ups or downs.';
    return 'Focus on stability rather than forcing a big change right now.';
  }
  if (score >= 80) return '승진이나 이직, 새로운 기회가 찾아올 거예요!';
  if (score >= 65) return '현재 업무에서 성과를 인정받을 수 있어요.';
  if (score >= 50) return '무난하게 업무를 진행할 수 있어요.';
  return '무리한 변화보다는 현재 위치에서 안정을 취하세요.';
}

function getLoveMessage(score: number, _stemDiff: number, lang: 'ko' | 'en' = 'ko'): string {
  if (lang === 'en') {
    if (score >= 80) return 'A new connection may emerge or an existing relationship will deepen!';
    if (score >= 65) return 'Current relationships become more stable and mature.';
    if (score >= 50) return 'Enjoy a calm and peaceful romantic or married life.';
    return 'Understanding and considerate communication is key this year.';
  }
  if (score >= 80) return '새로운 인연이 생기거나 관계가 깊어질 거예요!';
  if (score >= 65) return '현재 관계가 더 안정되고 성숙해져요.';
  if (score >= 50) return '평온한 연애/결혼 생활을 즐기세요.';
  return '서로의 차이를 이해하고 배려하는 게 중요해요.';
}

function getMoneyMessage(score: number, _stemDiff: number, _isStrong: boolean, lang: 'ko' | 'en' = 'ko'): string {
  if (lang === 'en') {
    if (score >= 80) return 'Income may increase or unexpected gains could come your way!';
    if (score >= 65) return 'Steady income growth and stable financial management are possible.';
    if (score >= 50) return 'Avoid risky investments and focus on saving.';
    return 'Keep a close eye on expenses and avoid major investments.';
  }
  if (score >= 80) return '수입이 늘거나 예상 밖의 수익이 생길 수 있어요!';
  if (score >= 65) return '꾸준한 수입 증가와 안정적인 재정 운영이 가능해요.';
  if (score >= 50) return '무리한 투자는 피하고 저축에 집중하세요.';
  return '지출 관리에 신경 쓰고 큰 투자는 피하세요.';
}

function getHealthMessage(score: number, _stemDiff: number, lang: 'ko' | 'en' = 'ko'): string {
  if (lang === 'en') {
    if (score >= 80) return 'Energy is high and health management will go smoothly!';
    if (score >= 65) return 'Generally healthy — maintain regular routines.';
    if (score >= 50) return 'Minor fatigue may accumulate — make sure to rest.';
    return 'Pay close attention to your energy levels and schedule regular check-ups.';
  }
  if (score >= 80) return '활력이 넘치고 건강 관리도 잘 될 거예요!';
  if (score >= 65) return '전반적으로 건강하니 규칙적인 생활을 유지하세요.';
  if (score >= 50) return '작은 피로는 쌓일 수 있으니 휴식을 챙기세요.';
  return '체력 관리에 유의하고 정기 검진을 받으세요.';
}

// 월별 운세 생성
function generateMonthlyFortune(saju: SajuResult, lang: 'ko' | 'en' = 'ko'): MonthlyFortune[] {
  const months: MonthlyFortune[] = [];
  const baseStem = 2; // 병년 기준
  const baseBranch = 6; // 오년 기준

  const highlightsKo = ['새로운 시작', '인연운 상승', '재물운 상승', '커리어 기회', '휴식 필요', '소통의 달', '변화의 기회', '안정의 달', '결단의 달', '수확의 달', '정리의 달', '마무리'];
  const highlightsEn = ['New Beginnings', 'Relationship Fortune', 'Financial Rise', 'Career Opportunity', 'Rest & Recharge', 'Communication', 'Change & Growth', 'Stability', 'Decision Time', 'Harvest Season', 'Declutter', 'Wrap Up'];

  const messagesKo = [
    '새로운 시작을 하기 좋은 달이에요.',
    '사람들과의 만남이 활발해지는 달이에요.',
    '금전운이 상승하는 달이에요.',
    '직장에서 기회가 찾아올 수 있어요.',
    '휴식과 재충전이 필요한 달이에요.',
    '대화와 소통이 중요한 달이에요.',
    '변화를 시도하기 좋은 달이에요.',
    '현상 유지에 집중하세요.',
    '중요한 결정을 내려야 할 수 있어요.',
    '노력한 결과를 수확하는 달이에요.',
    '정리와 정돈이 필요한 달이에요.',
    '한 해를 마무리하며 되돌아보세요.',
  ];
  const messagesEn = [
    'A great month for fresh starts and new initiatives.',
    'Social connections and new meetings are more active this month.',
    'Financial fortune is on the rise this month.',
    'Career opportunities may present themselves at work.',
    'Rest and recharging your energy is the priority.',
    'Communication and honest dialogue matter most now.',
    'A good time to try something new or make a change.',
    'Focus on maintaining your current position.',
    'You may need to make an important decision soon.',
    "It's time to reap the rewards of your hard work.",
    'Decluttering and getting organized will bring clarity.',
    "Reflect on the year as it comes to a close.",
  ];

  const highlights = lang === 'en' ? highlightsEn : highlightsKo;
  const messages = lang === 'en' ? messagesEn : messagesKo;

  for (let i = 0; i < 12; i++) {
    const monthStem = (baseStem + i) % 10;
    const monthBranch = (baseBranch + i) % 12;

    // 월별 점수 계산 (간단한 변동)
    const variation = Math.sin((i / 12) * Math.PI * 2) * 15;
    const score = Math.min(100, Math.max(30, Math.round(60 + variation)));

    months.push({
      month: i + 1,
      ganji: `${STEMS[monthStem]}${BRANCHES[monthBranch]}`,
      score,
      highlight: highlights[i],
      message: messages[i],
    });
  }

  return months;
}

// 조언 생성
function generateAdvice(
  level: SewoonResult['overallLevel'],
  stemDiff: number,
  _saju: SajuResult,
  lang: 'ko' | 'en' = 'ko'
): string[] {
  const advice: string[] = [];

  if (lang === 'en') {
    if (level === 'excellent' || level === 'good') {
      advice.push('Good energy is with you — seize opportunities proactively.');
      advice.push("Don't be afraid to take on new challenges.");
    } else if (level === 'normal') {
      advice.push('Focus on consolidating what you have rather than seeking big change.');
      advice.push('Consistent effort will bear fruit.');
    } else {
      advice.push('Avoid impulsive decisions and act with care.');
      advice.push('Taking care of your health and maintaining mental calm is the priority.');
    }
    if ([4, 5].includes(stemDiff)) advice.push('Wealth fortune is favorable — consider investments or financial planning.');
    if ([6, 7].includes(stemDiff)) advice.push('Career opportunities may arise — be prepared.');
    if ([8, 9].includes(stemDiff)) advice.push('Support from others is available this year. Cherish your connections.');
  } else {
    if (level === 'excellent' || level === 'good') {
      advice.push('좋은 기운이 함께하니 적극적으로 기회를 잡으세요.');
      advice.push('새로운 도전도 두려워하지 마세요.');
    } else if (level === 'normal') {
      advice.push('큰 변화보다는 현재를 다지는 데 집중하세요.');
      advice.push('꾸준한 노력이 결실을 맺을 거예요.');
    } else {
      advice.push('무리한 결정은 피하고 신중하게 행동하세요.');
      advice.push('체력 관리와 마음의 여유를 가지는 게 중요해요.');
    }
    if ([4, 5].includes(stemDiff)) advice.push('재물운이 좋으니 투자나 재테크에 관심을 가져보세요.');
    if ([6, 7].includes(stemDiff)) advice.push('직장에서 기회가 올 수 있으니 준비하세요.');
    if ([8, 9].includes(stemDiff)) advice.push('주변의 도움을 받을 수 있는 해예요. 인연을 소중히 하세요.');
  }

  return advice;
}

// 행운 정보
function generateLuckyInfo(saju: SajuResult, yearElement: Element, lang: 'ko' | 'en' = 'ko'): SewoonResult['lucky'] {
  const dayElement = STEM_ELEMENTS[saju.dayPillar.stemIdx];

  const colorMapKo: Record<Element, string[]> = {
    목: ['초록', '청록'],
    화: ['빨강', '주황', '분홍'],
    토: ['노랑', '베이지', '갈색'],
    금: ['흰색', '은색', '금색'],
    수: ['검정', '남색', '보라'],
  };
  const colorMapEn: Record<Element, string[]> = {
    목: ['Green', 'Teal'],
    화: ['Red', 'Orange', 'Pink'],
    토: ['Yellow', 'Beige', 'Brown'],
    금: ['White', 'Silver', 'Gold'],
    수: ['Black', 'Navy', 'Purple'],
  };

  const directionMapKo: Record<Element, string[]> = {
    목: ['동쪽'], 화: ['남쪽'], 토: ['중앙', '남서', '북동'], 금: ['서쪽'], 수: ['북쪽'],
  };
  const directionMapEn: Record<Element, string[]> = {
    목: ['East'], 화: ['South'], 토: ['Center', 'SW', 'NE'], 금: ['West'], 수: ['North'],
  };

  const neededElement = SHENG_CYCLE[dayElement] === yearElement ? dayElement : SHENG_CYCLE[dayElement];
  const colorMap = lang === 'en' ? colorMapEn : colorMapKo;
  const directionMap = lang === 'en' ? directionMapEn : directionMapKo;

  return {
    colors: colorMap[neededElement] || colorMap['토'],
    numbers: [3, 6, 9, 12, 15, 21, 27, 33],
    directions: directionMap[neededElement] || directionMap['토'],
  };
}